<template>
  <div>
    <!-- Breadcrumb -->
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="Alerting" label="Alerts" />
      <breadcrumb-link view="AlertCreate" label="New Alert" :active="true" />
    </breadcrumb-list>

    <section>
      <div class="container is-max-hd">
        <div class="columns is-flex-wrap-wrap">

          <!-- Form Column -->
          <div class="column is-two-thirds-desktop form-column">
            <div class="box" style="position: relative">

              <div class="header-container">
                <h1 class="title">Create Alert</h1>
              </div>

              <hr />

              <!-- Loading Overlay -->
              <b-loading :active="loadingDropdowns" :is-full-page="false" />

              <form @submit.prevent="handleSubmit">

                <fieldset :disabled="loadingDropdowns || submitting">
                  <!-- Criteria -->
                  <h4 class="title is-6 has-text-grey">
                    Alert Criteria (choose at least one)
                  </h4>

                  <p v-if="errors.criteria" class="has-text-danger mb-3">
                    {{ errors.criteria }}
                  </p>

                  <div class="form-container">
                   <b-field label="Provider Name">
                      <b-select v-model="form.provider_id" expanded :loading="loadingProviders">
                        <option :value="null" disabled selected>
                          Select Provider
                        </option>

                        <option v-for="provider in providerSuggestions" :key="provider.id" :value="provider.id">
                          {{ provider.name }}
                        </option>
                      </b-select>
                    </b-field>
                    
                    <div class="form-field" v-for="item in autocompleteFields" :key="item.model">
                      <b-field :label="item.label">
                        <b-autocomplete v-model="selected[item.model]"
                          :data="item.suggestions ? $data[item.suggestions] : []" :field="item.field"
                          :placeholder="`Type to search ${item.label} (min 2 chars)`" 
                          :loading="loadingSearch" :min-length="2"
                          :disabled="item.model === 'payer_id' && isPrefixActive"
                          @typing="term => searchAutocomplete(term, item.endpoint, item.suggestions)"
                          @select="selectedItem => onSelect(item.model, selectedItem)"
                          @input="value => onInput(item.model, value)"
                          :class="{ 'is-disabled-field': item.model === 'payer_id' && isPrefixActive }">
                          <template #empty>No results found</template>
                        </b-autocomplete>
                      </b-field>
                      <p v-if="item.model === 'payer_id' && isPrefixActive" class="is-size-7 mt-1">
                        <b-icon type="is-warning" icon="alert" size="is-small" class="warning-icon" />
                        Payer disabled when using prefix.
                      </p>

                      <p v-if="item.model === 'prefix_id' && isPrefixActive" class="is-size-7 mt-1">
                        <b-icon type="is-warning" icon="alert" size="is-small" class="warning-icon" />
                        Note: This alert will appear only if the selected prefix matches part of the
                        Member ID.
                      </p>
                    </div>

                  </div>

                  <hr />
                  <!-- Alert Message -->
                  <b-field label="Alert Message"
                    :message="errors.message || 'This text will appear on matching inquiries.'"
                    :type="{ 'is-danger': errors.message }">
                    <b-input v-model="form.message" type="textarea" placeholder="Enter alert message" maxlength="500"
                      required />
                  </b-field>

                  <!-- Alert Color -->
                  <h4 class="title is-6 has-text-grey mt-4">
                    Alert Color
                  </h4>

                  <b-field :message="errors.color" :type="{ 'is-danger': errors.color }"
                    style="width: 240px;margin-left: -10px;">
                    <div class="pill-color-picker">
                      <span v-for="color in presetColors" :key="color.value" class="color-dot"
                        :class="{ active: form.color === color.value }" :style="{ backgroundColor: color.hex }"
                        @click="form.color = color.value" />

                      <!-- Custom Color -->
                      <div class="color-dot custom">
                        <input type="color" v-model="customColor" class="native-color-input" @input="onCustomColor" />
                        <span class="plus">+</span>
                      </div>
                    </div>
                  </b-field>

                  <!-- Submit -->
                  <div class="submit-button-container">
                    <b-button type="is-primary" native-type="submit" size="is-medium" :loading="submitting"
                      :disabled="submitting" class="submit-button">
                      Create Alert
                    </b-button>
                  </div>

                </fieldset>
              </form>
            </div>
          </div>

          <!-- Instructions Column -->
          <div class="column instructions-column">
            <div class="box instructions-box">
              <h4 class="title is-5">Instructions</h4>
              <hr />
              <div class="content">
                <p>
                  Alerts help highlight important inquiries automatically.
                </p>
                <ul>
                  <li>Select at least one criteria</li>
                  <li>Alerts appear when an inquiry matches your criteria</li>
                  <li>Use color to indicate alert severity</li>
                </ul>
                <b-notification type="is-info is-light" :closable="false" class="mt-4">
                  Alerts highlight important inquiries so your team can act faster and streamline admissions
                </b-notification>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  </div>
</template>

<script>
import request from '../request';

export default {
  name: 'AlertCreate',

  data() {
    return {
      form: this.defaultForm(),
      selected: {
        payer_id: null,
        group_number_id: null,
        group_name_id: null,
        plan_name_id: null,
        plan_number_id: null,
        insurance_type_id: null,
      },
      presetColors: [
        { value: 'red', hex: '#f14668' },
        { value: 'green', hex: '#48c774' },
        { value: 'yellow', hex: '#ffdd57' },
      ],
      customColor: '#ffffff',
      loadingDropdowns: false,
      submitting: false,
      errors: {},
      loadingSearch: false,
      loadingProviders: false,
      payerSuggestions: [],
      groupSuggestions: [],
      groupNameSuggestions: [],
      planNameSuggestions: [],
      planNumberSuggestions: [],
      insuranceTypeSuggestions: [],
      providerSuggestions: [],
      prefixSuggestions: [],
      autocompleteFields: [
        { label: 'Prefix', model: 'prefix_id', suggestions: 'prefixSuggestions', field: 'prefix', endpoint: '/alerts/prefixes' },
        { label: 'Payer Name', model: 'payer_id', suggestions: 'payerSuggestions', field: 'name', endpoint: '/alerts/payers' },
        { label: 'Group Number', model: 'group_number_id', suggestions: 'groupSuggestions', field: 'group_number', endpoint: '/alerts/group-numbers' },
        { label: 'Group Name', model: 'group_name_id', suggestions: 'groupNameSuggestions', field: 'group_name', endpoint: '/alerts/group-names' },
        { label: 'Plan Name', model: 'plan_name_id', suggestions: 'planNameSuggestions', field: 'plan_name', endpoint: '/alerts/plan-names' },
        { label: 'Plan Number', model: 'plan_number_id', suggestions: 'planNumberSuggestions', field: 'plan_number', endpoint: '/alerts/plan-numbers' },
        { label: 'Insurance Type', model: 'insurance_type_id', suggestions: 'insuranceTypeSuggestions', field: 'name', endpoint: '/alerts/insurance-types' },
      ],
    };
  },
  computed: {
    isPrefixActive() {
      return !!this.form.prefix_id;
    },
  },
  async mounted() {
    await this.loadProviders();
  },
  methods: {
    defaultForm() {
      return {
        message: '',
        color: 'green',
        payer_id: null,
        group_number_id: null,
        group_name_id: null,
        plan_number_id: null,
        plan_name_id: null,
        insurance_type_id: null,
        prefix_id: null,
      };
    },

    onCustomColor() {
      this.form.color = this.customColor;
    },

    validateForm() {
      const errors = {};

      if (!this.form.message.trim()) {
        errors.message = 'Please enter an alert message.';
      }

      if (!this.form.color) {
        errors.color = 'Please select an alert color.';
      }

      const hasCriteria = Object.keys(this.form)
        .filter((key) => key.endsWith('_id'))
        .some((key) => this.form[key]);

      if (!hasCriteria) {
        errors.criteria =
          'Select at least one criteria so the alert knows when to trigger.';
      }

      return errors;
    },

    async handleSubmit() {

      this.submitting = true;
      this.errors = {};

      const validationErrors = this.validateForm();
      if (Object.keys(validationErrors).length > 0) {
        this.errors = validationErrors;
        this.submitting = false;
        return;
      }
      try {
        await request.post('/alerts', this.form);

        this.$buefy.toast.open({
          message: 'Alert created successfully',
          type: 'is-success',
        });

        this.$router.push({ name: 'Alerting' });
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response?.data?.error || 'Failed to create alert',
          type: 'is-danger',
        });
      } finally {
        this.submitting = false;
      }
    },
    async searchAutocomplete(term, endpoint, suggestionsKey) {
      if (term.length < 2) return;
      this.loadingSearch = true;
      try {
        const response = await request.get(endpoint, { params: { term } });
        switch (suggestionsKey) {
          case 'payerSuggestions':
            this.payerSuggestions = response.data.payers || [];
            break;
          case 'groupSuggestions':
            this.groupSuggestions = response.data.group_numbers || [];
            break;
          case 'groupNameSuggestions':
            this.groupNameSuggestions = response.data.group_names || [];
            break;
          case 'planNameSuggestions':
            this.planNameSuggestions = response.data.plan_names || [];
            break;
          case 'planNumberSuggestions':
            this.planNumberSuggestions = response.data.plan_numbers || [];
            break;
          case 'insuranceTypeSuggestions':
            this.insuranceTypeSuggestions = response.data.insurance_types || [];
            break;
          case 'providerSuggestions':
            this.providerSuggestions = response.data.providers || [];
            break;
          case 'prefixSuggestions':
            this.prefixSuggestions = response.data.prefixes || [];
            break;
          default:
            break;
        }

      } catch (error) {
        console.error(error);
        this.$buefy.toast.open({
          message: `Failed to search ${suggestionsKey}`,
          type: 'is-danger',
        });
      } finally {
        this.loadingSearch = false;
      }
    },
    onInput(field, value) {
    if (!value) {
      this.form[field] = null;
      this.selected[field] = null;
    }
  },
    onSelect(field, selectedItem) {
      this.form[field] = selectedItem.id;
      if (field === 'prefix_id') {
        this.form.payer_id = null;
        this.selected.payer_id = null;
      }
      switch (field) {
        case 'payer_id':
        case 'insurance_type_id':
          this.selected[field] = selectedItem.name;
          break;
        case 'group_number_id':
          this.selected[field] = selectedItem.group_number;
          break;
        case 'group_name_id':
          this.selected[field] = selectedItem.group_name;
          break;
        case 'plan_name_id':
          this.selected[field] = selectedItem.plan_name;
          break;
        case 'plan_number_id':
          this.selected[field] = selectedItem.plan_number;
          break;
      }
    },
    async loadProviders() {
      this.loadingProviders = true;
      try {
        const response = await request.get('/providers/compact');
        this.providerSuggestions = response.data.providers || [];
      } catch (e) {
        this.$buefy.toast.open({
          message: 'Failed to load providers',
          type: 'is-danger',
        });
      } finally {
        this.loadingProviders = false;
      }
    },
  },
};
</script>

<style scoped>
.box {
  padding: 2rem;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dropdown-label {
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.submit-button-container {
  margin-top: 2rem;
}

.submit-button {
  width: 100%;
}

/* Instructions */
.instructions-box {
  border: 1px solid #e9ecef;
}

/* Color Picker */
.pill-color-picker {
  display: flex;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 999px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.color-dot {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid transparent;
}

.color-dot.active {
  border-color: rgba(0, 0, 0, 0.35);
}



.native-color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.color-dot.custom {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg,
      #ff0080,
      #ff8c00,
      #40e0d0,
      #8a2be2);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.plus {
  color: white;
  font-weight: bold;
  font-size: 22px;
  pointer-events: none;
  transform: translateY(-1.5px);
}

.is-disabled-field {
  opacity: 0.6;
  pointer-events: none;
}
.warning-icon {
  font-size: 1.15rem; 
}


</style>
